// @flow
import {
  createSelector,
} from 'reselect';
import {
  DateTime,
} from 'luxon';
import * as R from 'ramda';

import {
  getResourceMap,
  getResourceMappedList,
} from './resources';
import {
  getProfile,
} from './profile';

import type {
  UiState,
} from '../types';


export const getUiState = (keyOrKeys: string | Array<string>) => (
  ({ ui }: { ui: UiState }) => (
    Array.isArray(keyOrKeys)
      ? (
        keyOrKeys.reduce(
          (acc, key) => ({
            ...acc,
            [key]: ui[key],
          }),
          {},
        )
      )
      : ui[keyOrKeys]
  )
);

const getGroupableResources = createSelector(
  [
    getResourceMappedList('issues', 'issues'),
    getResourceMappedList('users', 'all'),
    getResourceMappedList('projects', 'all'),
    getResourceMappedList('versions', 'all'),
  ],
  (
    issues,
    users,
    projects,
    versions,
  ) => ({
    issues,
    users,
    projects,
    versions,
  }),
);

const dateFilter = (
  interval => (
    worklog => (
      interval.contains(DateTime.fromISO(worklog.started))
    )
  )
);

const getWorklogsDateFiltered = createSelector(
  [
    getResourceMap('worklogs'),
    getUiState('calendarDate'),
  ],
  (
    allWorklogs,
    interval,
  ) => (
    R.pickBy(
      dateFilter(interval),
      allWorklogs,
    )
  ),
);

const orderByItems = {
  Created: i => new Date(i.fields.created),
  Key: i => i.key.split('-').map((item, index) => (index ? Number(item) : item)),
  'Last viewed': i => i.fields.lastViewed,
  Priority: i => i.fields.priority.id,
  Resolved: i => i.fields.reslution?.name,
  Status: i => i.fields.status?.name,
  Updated: i => i.fields.updated,
};

export const getSidebarItems = createSelector(
  [
    getGroupableResources,
    getResourceMap('issues'),
    getUiState('sidebarSearch'),
    getUiState('groupBy'),
    getUiState('orderBy'),
    getUiState('orderByType'),
    getWorklogsDateFiltered,
    ({ filters }) => filters,
    getProfile,
  ],
  (
    resources,
    issues,
    search,
    groupBy,
    orderBy,
    orderByType,
    worklogs,
    filtersRaw,
    profile,
  ) => {
    const {
      filterType: garbage,
      ...filters
    } = filtersRaw;
    switch (groupBy) {
      case 'issues':
        return (
          search.length
            ? (
              resources.issues.filter(
                issue => (
                  issue.fields.summary.toLowerCase().includes(search.toLowerCase())
                  || issue.key.toLowerCase().includes(search.toLowerCase())
                ),
              )
            )
            : (
              resources.issues
            )
        ).sort((a, b) => {
          try {
            const fieldA = orderByItems[orderBy](a);
            const fieldB = orderByItems[orderBy](b);
            const filterType = (
              Array.isArray(fieldA)
                ? `${orderByType}_${((fieldA[0] >= fieldB[0]) && (fieldA[1] >= fieldB[1]))}`
                : `${orderByType}_${(fieldA > fieldB)}`
            );
            const res = (() => {
              switch (filterType) {
                case 'ascending_true': {
                  return -1;
                }
                case 'ascending_false': {
                  return 1;
                }
                case 'descending_true': {
                  return 1;
                }
                case 'descending_false': {
                  return -1;
                }
                default: {
                  return 0;
                }
              }
            })();
            return res;
          } catch (err) {
            console.log(err);
          }

          return 0;
        });
      case 'users': {
        const filterUsers = [...filters.users];
        if (
          !filterUsers.length
          && (
            Object.keys(filters).reduce(
              (acc, filterName) => (
                acc
                && filters[filterName].length === 0
              ),
              true,
            )
          )
        ) {
          filterUsers.push(
            profile?.userData?.accountId
            || profile?.userData?.key,
          );
        }
        const users = (
          filterUsers.length
            ? (
              resources.users.filter(
                user => (
                  filterUsers.includes(
                    user?.accountId
                    || user?.key,
                  )
                ),
              )
            ) : resources.users
        );
        const worklogsArr = R.values(worklogs);
        return R.values(
          R.pickBy(
            user => (
              R.contains(
                (user.accountId || user.key),
                R.keys(
                  R.groupBy(
                    w => (
                      w.author.accountId || w.author.key
                    ),
                    worklogsArr,
                  ),
                ),
              )
            ),
            search.length
              ? (
                users.filter(
                  user => (
                    (user.accountId || user.key).toLowerCase().includes(search.toLowerCase())
                    || user.name.toLowerCase().includes(search.toLowerCase())
                    || user.displayName.toLowerCase().includes(search.toLowerCase())
                    || user.emailAddress.toLowerCase().includes(search.toLowerCase())
                  ),
                )
              )
              : (
                users
              ),
          ),
        ).map(
          user => ({
            ...user,
            totalTimeSeconds: (
              worklogsArr.reduce(
                (acc, worklog) => (
                  acc + (
                    worklog?.author?.accountId === user?.accountId
                      ? worklog.timeSpentSeconds
                      : 0
                  )
                ),
                0,
              )
            ),
          }),
        );
      }
      case 'projects': {
        const worklogsArr = R.values(worklogs);
        return R.values(
          R.pickBy(
            proj => R.contains(
              proj.key,
              R.keys(
                R.groupBy(
                  i => (
                    R.path(['fields', 'project', 'key'], i)
                  ),
                  resources.issues,
                ),
              ),
            ),
            search.length
              ? (
                resources.projects.filter(
                  project => (
                    project.key.toLowerCase().includes(search.toLowerCase())
                    || project.name.toLowerCase().includes(search.toLowerCase())
                  ),
                )
              )
              : (
                resources.projects
              ),
          ),
        ).map(
          project => ({
            ...project,
            totalTimeSeconds: (
              worklogsArr.reduce(
                (acc, worklog) => (
                  acc + (
                    issues[worklog.issueId].fields.project.id === project.id
                      ? worklog.timeSpentSeconds
                      : 0
                  )
                ),
                0,
              )
            ),
          }),
        );
      }
      default:
        return [];
    }
  },
);

export const getAllIssuesAsOptions = createSelector(
  getResourceMappedList('issues', 'issues'),
  issues => (
    issues.map(
      issue => ({
        label: `${issue.key} - ${issue.fields.summary}`,
        value: issue.id,
        issue,
      }),
    )
  ),
);
